// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { policyDocumentApi } from '../api/policyDocumentApi';
import type {
  PolicyDocumentVersionInfo,
  PolicyDocumentUploadResult,
  PolicyDocumentStatus,
  UploadProgress
} from '../types/policyDocument';

interface UsePolicyDocumentsReturn {
  versions: PolicyDocumentVersionInfo[];
  loading: boolean;
  error: string | null;
  hasDocuments: boolean;
  latestVersion: number | null;
  versionCount: number;
  documentStatus: PolicyDocumentStatus | null;
  uploading: boolean;
  uploadProgress: UploadProgress | null;
  uploadDocument: (file: File, changeSummary?: string) => Promise<PolicyDocumentUploadResult>;
  uploadDocumentOnBehalf: (advisorId: string, file: File, changeSummary?: string) => Promise<PolicyDocumentUploadResult>;
  downloadLatest: () => Promise<void>;
  downloadVersion: (version: number) => Promise<void>;
  deleteVersion: (version: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function usePolicyDocuments(policyId: string): UsePolicyDocumentsReturn {
  const [versions, setVersions] = useState<PolicyDocumentVersionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasDocuments, setHasDocuments] = useState(false);
  const [latestVersion, setLatestVersion] = useState<number | null>(null);
  const [versionCount, setVersionCount] = useState(0);
  const [documentStatus, setDocumentStatus] = useState<PolicyDocumentStatus | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const refresh = useCallback(async () => {
    if (!policyId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch existence info and versions in parallel
      const [existsResponse, versionsResponse] = await Promise.all([
        policyDocumentApi.checkExists(policyId),
        policyDocumentApi.listVersions(policyId).catch(() => [] as PolicyDocumentVersionInfo[])
      ]);

      setHasDocuments(existsResponse.hasDocuments);
      setLatestVersion(existsResponse.latestVersion);
      setVersionCount(existsResponse.versionCount);
      setDocumentStatus(existsResponse.documentStatus);
      setVersions(versionsResponse);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to fetch policy documents:', err);
      setError(error.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  const uploadDocument = useCallback(async (
    file: File,
    changeSummary?: string
  ): Promise<PolicyDocumentUploadResult> => {
    setUploading(true);
    setUploadProgress(null);
    setError(null);

    try {
      const result = await policyDocumentApi.uploadDocument(
        policyId,
        file,
        changeSummary,
        (progress) => setUploadProgress(progress)
      );

      // Refresh the version list after upload
      await refresh();

      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; code?: string } } };
      console.error('Failed to upload document:', err);
      const errorMessage = error.response?.data?.message || 'Failed to upload document';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [policyId, refresh]);

  const uploadDocumentOnBehalf = useCallback(async (
    advisorId: string,
    file: File,
    changeSummary?: string
  ): Promise<PolicyDocumentUploadResult> => {
    setUploading(true);
    setUploadProgress(null);
    setError(null);

    try {
      const result = await policyDocumentApi.uploadDocumentOnBehalf(
        policyId,
        advisorId,
        file,
        changeSummary,
        (progress) => setUploadProgress(progress)
      );

      // Refresh the version list after upload
      await refresh();

      return result;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; code?: string } } };
      console.error('Failed to upload document on behalf:', err);
      const errorMessage = error.response?.data?.message || 'Failed to upload document';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [policyId, refresh]);

  const downloadLatest = useCallback(async () => {
    try {
      const { blob, filename } = await policyDocumentApi.downloadLatest(policyId);

      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to download document:', err);
      setError(error.response?.data?.message || 'Failed to download document');
      throw err;
    }
  }, [policyId]);

  const downloadVersion = useCallback(async (version: number) => {
    try {
      const { blob, filename } = await policyDocumentApi.downloadVersion(policyId, version);

      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to download document version:', err);
      setError(error.response?.data?.message || 'Failed to download document');
      throw err;
    }
  }, [policyId]);

  const deleteVersion = useCallback(async (version: number) => {
    try {
      await policyDocumentApi.deleteVersion(policyId, version);

      // Refresh the version list after deletion
      await refresh();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Failed to delete document version:', err);
      setError(error.response?.data?.message || 'Failed to delete document version');
      throw err;
    }
  }, [policyId, refresh]);

  return {
    versions,
    loading,
    error,
    hasDocuments,
    latestVersion,
    versionCount,
    documentStatus,
    uploading,
    uploadProgress,
    uploadDocument,
    uploadDocumentOnBehalf,
    downloadLatest,
    downloadVersion,
    deleteVersion,
    refresh
  };
}
